<template>
	<div class="container w-100" data-cy="veracross-component">
		<v-card class="mb-4">
			<v-card-title>Veracross Configuration</v-card-title>
			<v-card-text>
				<v-form>
					<v-row>
						<v-col cols="12" md="6">
							<v-text-field
								:model-value="config.school_route"
								label="School route"
								variant="outlined"
								density="comfortable"
								:loading="loading"
								data-cy="veracross-school-route"
								@update:model-value="update({ school_route: String($event ?? '') })"
							></v-text-field>
						</v-col>
						<v-col cols="12" md="6">
							<v-text-field
								:model-value="config.client_id"
								label="Client ID"
								variant="outlined"
								density="comfortable"
								:loading="loading"
								data-cy="veracross-client-id"
								@update:model-value="update({ client_id: String($event ?? '') })"
							></v-text-field>
						</v-col>
						<v-col cols="12" md="6">
							<v-text-field
								:model-value="config.client_secret"
								label="Client secret"
								type="password"
								autocomplete="new-password"
								variant="outlined"
								density="comfortable"
								:loading="loading"
								data-cy="veracross-client-secret"
								@update:model-value="update({ client_secret: String($event ?? '') })"
							></v-text-field>
						</v-col>
						<v-col cols="12" md="6">
							<v-select
								:model-value="config.school_id"
								:items="schoolOptions"
								item-title="school_name"
								item-value="school_id"
								label="School"
								variant="outlined"
								density="comfortable"
								:loading="loading"
								data-cy="veracross-school"
								:menu-props="{ ['data-cy' as string]: 'veracross-school-menu' }"
								@update:model-value="update({ school_id: $event ?? null })"
							></v-select>
						</v-col>
						<v-col cols="12" md="6">
							<v-text-field
								:model-value="config.school_year"
								label="School year (optional)"
								variant="outlined"
								density="comfortable"
								:loading="loading"
								data-cy="veracross-school-year"
								@update:model-value="update({ school_year: String($event ?? '') })"
							></v-text-field>
						</v-col>
						<v-col cols="12" md="6">
							<v-select
								:model-value="config.parent_filter"
								:items="veracrossParentFilters"
								item-title="title"
								item-value="value"
								label="Parent filter"
								variant="outlined"
								density="comfortable"
								:loading="loading"
								data-cy="veracross-parent-filter"
								:menu-props="{
									['data-cy' as string]: 'veracross-parent-filter-menu',
								}"
								@update:model-value="setParentFilter"
							></v-select>
						</v-col>
					</v-row>
				</v-form>
			</v-card-text>
		</v-card>
		<ecsTaskSync integration_type="veracross" :has-valid-config="hasValidConfig" />
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from "vue";
import EcsTaskSync from "@/components/school/integration/ecsTaskSync.vue";
import {
	veracrossParentFilters,
	type VeracrossConfig,
	type VeracrossParentFilter,
} from "@/components/school/integration/utils";
import { useDistrictStore } from "@/stores/district";
import { useSchoolStore } from "@/stores/school";

const props = defineProps<{
	config: VeracrossConfig;
	hasValidConfig: boolean;
	loading: boolean;
}>();

const emit = defineEmits<{
	(e: "update:config", value: VeracrossConfig): void;
}>();

const districtStore = useDistrictStore();
const schoolStore = useSchoolStore();

const schoolOptions = computed(() => {
	const options: { school_id: number; school_name: string }[] = [];
	const add = (id?: number | null, name?: string | null) => {
		if (!id || options.some((option) => option.school_id === id)) return;
		options.push({
			school_id: id,
			school_name: name?.trim() ? name : `School ${id}`,
		});
	};

	add(schoolStore.school?.school_id, schoolStore.school?.school_name);
	for (const school of districtStore.schools || []) {
		add(school.school_id, school.school_name);
	}
	add(props.config.school_id);
	return options;
});

const update = (patch: Partial<VeracrossConfig>) => {
	emit("update:config", { ...props.config, ...patch });
};

const setParentFilter = (value: VeracrossParentFilter) => {
	update({ parent_filter: value });
};

watch(schoolOptions, (options) => {
	if (!props.config.school_id && options.length === 1) {
		update({ school_id: options[0].school_id });
	}
});

onMounted(async () => {
	await districtStore.fetchSchools();
	if (!props.config.school_id && schoolOptions.value.length === 1) {
		update({ school_id: schoolOptions.value[0].school_id });
	}
});
</script>

<style scoped lang="scss"></style>
